import type { ColDef } from 'ag-grid-community';
import { get, isUndefined, omitBy } from 'lodash-es';
import { getIntlKey } from '../../../contexts';
import { isKeyIn, prettyName } from '../../../utils';
import { envConfig } from '../../../utils/envConfig';
import { wrapColumnWithExcelColumnClass } from '../useBlotterTable/excelExportStyling';
import { messages } from './columnHeaderTranslations';
import { getAgGridColId } from './getAgGridColId';
import type { ColumnType } from './index';
import type { Column } from './types';

/**
 * Create a base ColDef for AgGrid.
 *
 * @param column Column definition
 * @param ignoreDefaultHeaderValue Ignore the default title generated by the headerValueGetter if a translation is missing.
 */
export function baseColumn(column: Column, ignoreDefaultHeaderValue = false): ColDef {
  // Prevent clearing out values from `column` that are undefined
  return omitBy(
    {
      colId: getAgGridColId(column),
      field: column.field,

      initialWidth: column.initialWidth,
      initialFlex: column.initialFlex,
      initialHide: column.initialHide,
      initialPinned: column.initialPinned,
      initialSort: column.initialSort === '+' ? 'asc' : column.initialSort === '-' ? 'desc' : undefined,
      initialSortIndex: column.initialSortIndex,
      initialRowGroup: column.initialRowGroup,
      initialRowGroupIndex: column.initialRowGroupIndex,
      initialAggFunc: column.initialAggFunc,

      width: column.width,
      flex: column.flex,
      hide: column.hide,
      pinned: column.pinned,
      sort: column.sort === '+' ? 'asc' : column.sort === '-' ? 'desc' : undefined,
      sortIndex: column.sortIndex,
      rowGroup: column.rowGroup,
      rowGroupIndex: column.rowGroupIndex,
      aggFunc: column.aggFunc,

      lockPosition: column.lockPosition,
      maxWidth: column.maxWidth,
      minWidth: column.minWidth,
      headerValueGetter: params => {
        if (typeof column.headerValueGetter === 'function') {
          return column.headerValueGetter(params);
        }
        const { context } = params;
        const customIntlKey =
          typeof column.title === 'object' && column.title.intlKey ? column.title.intlKey : undefined;
        const effectiveField = column.field ?? column.id;
        const intlKey = customIntlKey ?? (effectiveField ? getIntlKey(effectiveField) : undefined);
        if (typeof column.title === 'string') {
          // Column header overwritten with title prop
          return column.title;
        } else if (intlKey && isKeyIn(intlKey, messages)) {
          // Column header has translation
          return context.current.intl.formatMessage(messages[intlKey], column.titleIntlKeyValues);
        }
        // Columns without a "field" don't have titles. They are used for buttons, etc.
        // If passed a title, it will be used instead.
        else if (!column.field) {
          return undefined;
        }
        // Useful to ignore the warning if you are intentionally not providing a title (but you did provide a field)
        else if (ignoreDefaultHeaderValue) {
          return undefined;
        }
        // Default behavior, determined by field. "LastUpdateTime" --> "Last Update Time"
        // We don't consider column.id here because some columns intentionally leave it undefined
        // This way, they don't render any column header name. Example: { id: 'edit-button' }
        const defaultTitle = prettyName(column.field);
        if (envConfig.VITE_AVA_ENV === 'local' && import.meta.env.VITE_APP !== 'ava') {
          console.warn(
            `Internationalization Missing: Column Title with intlKey "${intlKey}" will render as "${defaultTitle}"`
          );
        }
        return defaultTitle;
      },
      headerTooltip: column.description,
      headerComponent: column.headerGroupToggle ? 'groupToggleHeader' : undefined,
      headerComponentParams: column.columnsInGroup ? { columnsInGroup: column.columnsInGroup } : undefined,
      sortable: column.sortable ?? true,
      editable: column.editable,
      resizable: !column.frozen,
      lockVisible: column.frozen,
      lockPinned: column.frozen,
      suppressMovable: column.frozen,
      checkboxSelection: column.checkboxSelection,
      showDisabledCheckboxes: column.showDisabledCheckboxes,
      headerCheckboxSelection: column.headerCheckboxSelection,
      enableRowGroup: column.enableRowGroup,
      suppressHeaderMenuButton: column.suppressHeaderMenuButton,
      suppressColumnsToolPanel: column.suppressColumnsToolPanel,
      tooltipField: column.tooltipField,
      tooltipValueGetter: column.tooltipValueGetter,
      tooltipComponent: column.tooltipComponent,
      cellStyle: column.cellStyle,
      suppressKeyboardEvent: column.suppressKeyboardEvent,
      suppressNavigable: column.suppressNavigable,
      headerClass: () => {
        return column.columnGroup ? `ag-custom-column-group ${column.headerClass ?? ''}` : column.headerClass;
      },
      rowSpan: column.rowSpan,
      cellClass: wrapColumnWithExcelColumnClass(column),
      cellClassRules: column.cellClassRules,

      comparator: (a, b) => {
        if (typeof a === 'string') {
          return a.localeCompare(b);
        }
        if (typeof b === 'string') {
          return -1 * b.localeCompare(a);
        }
        return a - b;
      },
      /**
       * This is the column's own context. Should not be confused with the entire Grid's context.
       */
      context: {
        [COLDEF_CUSTOM_KEY]: {
          exportable: column.exportable,
          talosColumnType: column.type,
          dynamics: column.dynamics,
          talosColumnParams: column.params,
        } satisfies ColDefCustomProperties,
      },
    } as ColDef,
    isUndefined
  );
}

const COLDEF_CUSTOM_KEY = '__custom__';

export interface ColDefCustomProperties {
  exportable?: boolean;
  talosColumnType?: ColumnType;
  dynamics?: Column['dynamics'];
  talosColumnParams?: Column['params'];
}

export function getCustomColDefProperties(colDef: ColDef | undefined): ColDefCustomProperties | undefined {
  return get(colDef, `context.${COLDEF_CUSTOM_KEY}`);
}
